/* The four chart shapes the panels use; no panel builds a Chart.js config itself.
 *
 * Each memoises its config on the values it was given rather than on array identity, and
 * holds `format` in a ref, because call sites pass freshly-built arrays and arrow functions
 * on every render — a config keyed on those is rebuilt mid-animation and leaves a blank plot.
 */

import { useMemo, useRef } from 'react';
import type { ChartConfiguration } from 'chart.js/auto';

import { num, pct } from '../lib/format';
import { useApp } from '../state/app';
import { categoryAxis, cssVar, hiddenValueAxis, ordinal } from './chart';
import { ChartCanvas, Legend, type TooltipContent, type TooltipRow } from './ChartCanvas';

export interface BarRow {
  label: string;
  value: number;
  /** Replaces the single "Value" row in the tooltip. */
  detail?: TooltipRow[];
}

export interface Series {
  name: string;
  color: string;
  values: (number | null)[];
}

type Format = (value: number | null) => string;

function Nothing({ message }: { message: string }) {
  return (
    <div className="py-10 text-center text-sm text-[var(--text-muted)]">{message}</div>
  );
}

/**
 * Horizontal bars for nominal categories. One colour for every bar, since the length already
 * encodes the value, and values are direct-labelled at the bar end.
 */
export function BarChart({
  rows,
  format = num,
  color,
  maxRows = 12,
  label,
}: {
  rows: BarRow[];
  format?: Format;
  color?: string;
  maxRows?: number;
  label: string;
}) {
  const { theme } = useApp();
  const data = rows.slice(0, maxRows);
  const key = JSON.stringify(data);
  const formatRef = useRef(format);
  formatRef.current = format;

  const config = useMemo<ChartConfiguration>(() => {
    const points = JSON.parse(key) as BarRow[];
    return {
      type: 'bar',
      data: {
        labels: points.map((d) => d.label),
        datasets: [
          {
            data: points.map((d) => d.value),
            backgroundColor: color || cssVar('--chart-series-1'),
            // Rounded at the data end only: the bar starts at the baseline.
            borderRadius: 4,
            borderSkipped: 'start',
            barThickness: 22,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        // Room for the direct labels, which sit outside the plot.
        layout: { padding: { right: 58 } },
        scales: { x: hiddenValueAxis(), y: categoryAxis() },
        // The whole row is the hit area, not the 22px bar.
        interaction: { mode: 'index', axis: 'y', intersect: false },
        plugins: {
          legend: { display: false },
          directLabels: { format: (value: number) => formatRef.current(value) },
        },
      },
    };
    // `theme` is not read directly — it is what makes the CSS variables above resolve to
    // the other palette, so the config has to be rebuilt when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, color, theme]);

  if (!data.length) return <Nothing message="No data in this window." />;

  return (
    <ChartCanvas
      height={6 + data.length * 30}
      config={config}
      label={label}
      tooltip={([point]): TooltipContent | null => {
        const row = data[point.dataIndex];
        if (!row) return null;
        return {
          title: row.label,
          rows: row.detail ?? [{ label: 'Value', value: format(row.value) }],
        };
      }}
    />
  );
}

/**
 * Multi-series line chart over discrete buckets (weeks or days). All series must share a
 * unit — there is deliberately no second axis.
 */
export function LineChart({
  labels,
  series,
  format = num,
  label,
}: {
  labels: string[];
  series: Series[];
  format?: Format;
  label: string;
}) {
  const { theme } = useApp();
  const key = JSON.stringify({ labels, series });
  const formatRef = useRef(format);
  formatRef.current = format;

  const config = useMemo<ChartConfiguration>(() => {
    const { labels: ticks, series: lines } = JSON.parse(key) as {
      labels: string[];
      series: Series[];
    };
    return {
      type: 'line',
      data: {
        labels: ticks,
        datasets: lines.map((s) => ({
          label: s.name,
          data: s.values,
          borderColor: s.color,
          backgroundColor: s.color,
          borderWidth: 2,
          tension: 0,
          spanGaps: true,
          pointRadius: 4,
          pointHoverRadius: 5,
          // A 2px surface ring, so overlapping markers stay separable without an outline.
          pointBorderColor: cssVar('--bg-elevated'),
          pointBorderWidth: 2,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: { display: false },
            border: { color: cssVar('--chart-baseline') },
            ticks: {
              color: cssVar('--text-muted'),
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
            },
          },
          y: {
            beginAtZero: true,
            // Recessive solid hairline grid — never dashed.
            grid: { color: cssVar('--chart-grid'), drawTicks: false },
            border: { display: false },
            ticks: {
              color: cssVar('--text-muted'),
              maxTicksLimit: 5,
              callback: (value: string | number) => formatRef.current(Number(value)),
            },
          },
        },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          crosshair: { enabled: true },
          // Selective direct label: the endpoint only, never a number on every point.
          directLabels: { mode: 'end', format: (value: number) => formatRef.current(value) },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, theme]);

  if (!labels.length) return <Nothing message="No data in this window." />;

  return (
    <>
      <ChartCanvas
        height={210}
        config={config}
        label={label}
        tooltip={(points): TooltipContent => ({
          title: points[0]?.label ?? '',
          rows: points.map((p) => ({
            label: series[p.datasetIndex]?.name ?? '',
            value: format(p.value),
            color: series[p.datasetIndex]?.color,
          })),
        })}
      />
      {series.length >= 2 && <Legend items={series} />}
    </>
  );
}

/** Grouped bars for an ordered measure (p50 / p95 / p99) — hence the ordinal ramp. */
export function GroupedBarChart({
  groups,
  series,
  format = num,
  label,
}: {
  groups: string[];
  series: Series[];
  format?: Format;
  label: string;
}) {
  const { theme } = useApp();
  const key = JSON.stringify({ groups, series });
  const formatRef = useRef(format);
  formatRef.current = format;

  const config = useMemo<ChartConfiguration>(() => {
    const { groups: names, series: bars } = JSON.parse(key) as {
      groups: string[];
      series: Series[];
    };
    return {
      type: 'bar',
      data: {
        labels: names,
        datasets: bars.map((s) => ({
          label: s.name,
          data: s.values,
          backgroundColor: s.color,
          borderRadius: 4,
          borderSkipped: 'start',
          // Of a 26px slot: the 2px gap is surface, not a stroke.
          barThickness: 24,
        })),
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 62 } },
        scales: { x: hiddenValueAxis(), y: categoryAxis(18) },
        interaction: { mode: 'nearest', axis: 'y', intersect: true },
        plugins: {
          legend: { display: false },
          directLabels: { format: (value: number) => formatRef.current(value) },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, theme]);

  if (!groups.length) return <Nothing message="No timings recorded." />;

  return (
    <>
      <ChartCanvas
        height={6 + groups.length * (series.length * 26 + 16)}
        config={config}
        label={label}
        tooltip={([point]): TooltipContent => ({
          title: groups[point.dataIndex] ?? '',
          rows: [
            {
              label: series[point.datasetIndex]?.name ?? '',
              value: format(point.value),
            },
          ],
        })}
      />
      <Legend items={series} />
    </>
  );
}

/** The escalation funnel: ordered stages, so the ordinal ramp again. */
export function FunnelChart({ steps, label }: { steps: BarRow[]; label: string }) {
  const { theme } = useApp();
  const key = JSON.stringify(steps);

  const config = useMemo<ChartConfiguration>(() => {
    const stages = JSON.parse(key) as BarRow[];
    const ramp = ordinal();
    return {
      type: 'bar',
      data: {
        labels: stages.map((s) => s.label),
        datasets: [
          {
            data: stages.map((s) => s.value),
            backgroundColor: stages.map((_, i) => ramp[i % ramp.length]),
            borderRadius: 4,
            borderSkipped: 'start',
            barThickness: 34,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 96 } },
        scales: { x: hiddenValueAxis(), y: categoryAxis(20) },
        plugins: {
          legend: { display: false },
          directLabels: {
            // The conversion from the previous stage is the number that matters, so it is
            // printed next to the count rather than left to be worked out.
            label: (value: number, index: number) => {
              if (!index) return num(value);
              const previous = stages[index - 1]?.value;
              return `${num(value)}  (${pct(previous ? value / previous : null)} of previous)`;
            },
          },
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, theme]);

  return <ChartCanvas height={steps.length * 44} config={config} label={label} />;
}
